import React, { useEffect, useState } from 'react';
import { Col, Row, Spinner } from 'react-bootstrap';
import Answer from './answer/Answer';
import Question from './question/Question';
import {
  useEndQuizMutation,
  useFindQuestionQuery,
  useSubmitQuestionAnswerMutation,
  useSubmitQuizMutation
} from 'service/quizAssessmentQuery/quizAssessmentQuery';
import { ErrorResponse, Quiz } from './quizCenter.type';
import {
  clearLocalByKey,
  getLocalStorage,
  setLocalStorage
} from 'helpers/localstorage-utils';
import { TOKEN_CONSTANTS } from 'constant';
import { useNavigate } from 'react-router-dom';
import { useSettingsPanelContext } from 'providers/SettingsPanelProvider';
import { useAppContext } from 'providers/AppProvider';
import QuizExpiredModal from 'components/modals/QuizExpiredModal';
import QuizSubmissionModal from 'components/modals/QuizSubmissionModal';

function QuizCenter() {
  const [step, setStep] = useState<number | undefined>(undefined);
  const [selectedAnswer, setSelectedAnswer] = useState<string[]>([]);
  const [isExpired, setIsExpired] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { setSettingsPanelConfig } = useSettingsPanelContext();
  const { setConfig } = useAppContext();

  const { data = {}, isFetching, isError, refetch } = useFindQuestionQuery('');
  const [submitQuestionAnswer, { isLoading }] =
    useSubmitQuestionAnswerMutation();
  const [submitQuiz, { isLoading: submitLoader }] = useSubmitQuizMutation();
  const [endQuiz] = useEndQuizMutation();

  const navigate = useNavigate();

  const setDefaultTheme = () => {
    setConfig({ navbarTopShape: 'default' });
    setSettingsPanelConfig({
      showSettingPanelButton: true
    });
  };

  const handleEndQuiz = (successCallback: () => void, isTimeEnd?: boolean) => {
    endQuiz(isTimeEnd)
      .unwrap()
      .then(() => {
        successCallback();
      })
      .catch(console.error);
  };

  const expiredFallback = () => {
    handleEndQuiz(() => {
      setIsExpired(true);
    }, true);
  };

  const expiredModalClose = () => {
    clearLocalByKey(TOKEN_CONSTANTS.QUIZ);
    setDefaultTheme();
    navigate('/home/ladder/basic');
  };

  const confirmFallback = () => {
    setIsSubmitted(false);
    clearLocalByKey(TOKEN_CONSTANTS.QUIZ);
    setDefaultTheme();
    navigate('/home/ladder/basic');
  };

  const errorFallback = (error: ErrorResponse) => {
    if (error.status === 401) {
      setIsExpired(true);
    }
  };

  const handleAnswerSelect = (value: string) => {
    setSelectedAnswer(prev => {
      let prevState = [...prev];
      const index = prevState.indexOf(value);
      if (index !== -1) {
        prevState.splice(index, 1);
      } else {
        if ((data as Quiz)?.question?.type === 'radio') {
          prevState = [value];
        } else {
          prevState.push(value);
        }
      }
      return prevState;
    });
  };

  const handleNext = (questionId: string | undefined) => {
    if (selectedAnswer.length > 0 && questionId) {
      submitQuestionAnswer({ id: questionId, answer: selectedAnswer })
        .unwrap()
        .then(() => {
          refetch();
          setSelectedAnswer([]);
        })
        .catch(errorFallback);
    }
  };

  const handleSubmit = (questionId: string | undefined) => {
    if (selectedAnswer.length > 0 && questionId) {
      submitQuiz({ id: questionId, answer: selectedAnswer })
        .unwrap()
        .then(() => {
          setIsSubmitted(true);
        })
        .catch(errorFallback);
    }
  };

  useEffect(() => {
    const responseData = data as Quiz;
    if (responseData.currentStep) {
      setStep(responseData.currentStep);
    }
  }, [(data as Quiz).currentStep]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      handleEndQuiz(() => {
        clearLocalByKey(TOKEN_CONSTANTS.QUIZ);
        setDefaultTheme();
      });
      const message = 'Are you sure you want to leave?';
      event.returnValue = message;
      return message;
    };

    // Store API request data when offline
    const handleOffline = () => {
      // Save request data to localStorage (you can use IndexedDB for more complex data)
      setLocalStorage(TOKEN_CONSTANTS.QUIZ_OFFLINE, '1');
    };

    const handlePopState = () => {
      // Show confirmation dialog
      const confirmBack = window.confirm(
        'Are you sure you want to leave this page?'
      );
      if (confirmBack) {
        // Allow back navigation if confirmed
        handleEndQuiz(() => {
          clearLocalByKey(TOKEN_CONSTANTS.QUIZ);
        });
        setDefaultTheme();
        navigate('/home/ladder/basic');
      } else {
        // Prevent back navigation by pushing the state back into history
        window.history.pushState(null, '', window.location.href);
      }
    };

    // Sync queued data when the user is online
    const handleOnline = () => {
      const pendingData = getLocalStorage(TOKEN_CONSTANTS.QUIZ_OFFLINE);
      if (pendingData === '1') {
        handleEndQuiz(() => {
          clearLocalByKey(TOKEN_CONSTANTS.QUIZ_OFFLINE);
          clearLocalByKey(TOKEN_CONSTANTS.QUIZ);
          setDefaultTheme();
          navigate('/home/ladder/basic');
        });
      }
    };
    window.history.pushState(null, '', window.location.href);
    // Add event listeners for offline and online status changes
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.onpopstate = handlePopState;

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      window.onpopstate = null;
    };
  }, []);

  if (isError) return null;

  return (
    <>
      <QuizExpiredModal show={isExpired} handleSubmit={expiredModalClose} />
      <QuizSubmissionModal show={isSubmitted} handleSubmit={confirmFallback} />
      <Row>
        {isFetching ? (
          <Col xs={12}>
            <div className="d-flex justify-content-center w-100">
              <Spinner animation="border" role="status" className="me-2">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          </Col>
        ) : (
          <>
            <Col xl={6}>
              <Question currentStep={step} data={data as Quiz} />
            </Col>
            <Col xl={6}>
              <Answer
                data={data as Quiz}
                onSelect={handleAnswerSelect}
                selectedAnswer={selectedAnswer}
                onNext={handleNext}
                answerSubmissionLoader={isLoading}
                currentStep={step}
                onSubmit={handleSubmit}
                submitLoader={submitLoader}
                onTimeEnd={expiredFallback}
              />
            </Col>
          </>
        )}
      </Row>
    </>
  );
}

export default QuizCenter;
